import type { WarehouseProblem } from '@server/warehouse-failure';
import { Code2, Sparkles } from 'lucide-react';
import { useMemo, useRef } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';

import { CopyButton } from '@/_shared/components/CopyButton';
import { Table } from '@/_shared/components/Table';
import { Button } from '@/_shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/_shared/components/ui/card';
import {
  CHART_PLOT_HEIGHT,
  ChartContainer,
  ChartLegendList,
  ChartTooltip,
  ChartTooltipContent,
} from '@/_shared/components/ui/chart';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/_shared/components/ui/dialog';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/_shared/components/ui/sheet';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { WarehouseProblemCallout } from '@/_shared/components/WarehouseProblemCallout/WarehouseProblemCallout';
import { useContainerWidth } from '@/_shared/hooks/use-container-width';
import { useIsMobile } from '@/_shared/hooks/use-mobile';
import { useSyntaxStyle } from '@/_shared/hooks/useSyntaxStyle';
import { type AxisConfig, buildXAxisConfig } from '@/_shared/lib/charts/axis';
import { CHART_COLORS } from '@/_shared/lib/charts/colors';
import { detectSeriesColumns, isNumeric, prepareChartData, toNumber } from '@/_shared/lib/charts/data';
import { buildLegendItems } from '@/_shared/lib/charts/legend';
import type { ChartConfig, PanelData } from '@/_shared/lib/charts/types';
import { coerceLabel } from '@/_shared/lib/coerce-label';
import { formatSql } from '@/_shared/lib/sql';
import { SyntaxHighlighter } from '@/_shared/lib/syntax';
import { deriveColumns } from '@/_shared/lib/table';
import { cn } from '@/_shared/lib/utils';

import type { DashboardPanel as PanelType } from '../types';
import { FunnelChart } from './FunnelChart';
import { MetricCard } from './MetricCard';

// ─── Props ───

interface Props {
  panel: PanelType;
  data: PanelData | null;
  loading?: boolean;
  /** Fetching new data while stale results are still in memory (e.g. invalidation). */
  refetching?: boolean;
  error?: string | null;
  /**
   * The server's description of a failed warehouse query (ACT2-1166). When present it is shown in place
   * of `error`, with the engine's own text under "Show details".
   */
  problem?: WarehouseProblem | null;
  /** Toolbar ✨ — attaches this panel to the composer. Same meaning whether or not the panel errored. */
  onAskAssistant?: () => void;
  /** Error-state CTA — sends the canned fix prompt straight away. Only rendered when `error` is set. */
  onFixError?: () => void;
}

// ─── Helpers ───

function buildChartConfig(seriesColumns: string[]): ChartConfig {
  const config: ChartConfig = {};
  for (let i = 0; i < seriesColumns.length; i++) {
    const col = seriesColumns[i]!;
    config[col] = {
      label: col,
      color: CHART_COLORS[i % CHART_COLORS.length]!,
    };
  }
  return config;
}

// ─── Chart Renderers ───

// Grid + axes + tooltip + legend, shared by the bar/line/area renderers. Returned
// as a fragment and embedded inline (not a `<Component/>`) so recharts flattens it
// and still discovers the axes among the chart's children.
function cartesianAxes(labelColumn: string, axis: AxisConfig): React.ReactElement {
  return (
    <>
      <CartesianGrid vertical={false} />
      <XAxis dataKey={labelColumn} tickLine={false} axisLine={false} {...axis.props} />
      <YAxis tickLine={false} axisLine={false} tickMargin={8} />
      <ChartTooltip content={<ChartTooltipContent labelFormatter={axis.tooltipLabelFormatter} />} />
    </>
  );
}

function renderBarChart(
  chartData: Record<string, unknown>[],
  labelColumn: string,
  seriesColumns: string[],
  config: ChartConfig,
  axis: AxisConfig,
  height: number,
): React.ReactElement {
  return (
    <ChartContainer config={config} className="w-full" chartHeight={height}>
      <BarChart data={chartData} accessibilityLayer>
        {cartesianAxes(labelColumn, axis)}
        {seriesColumns.map((col) => (
          <Bar key={col} dataKey={col} fill={config[col]?.color ?? CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
        ))}
      </BarChart>
    </ChartContainer>
  );
}

function renderLineChart(
  chartData: Record<string, unknown>[],
  labelColumn: string,
  seriesColumns: string[],
  config: ChartConfig,
  axis: AxisConfig,
  height: number,
): React.ReactElement {
  return (
    <ChartContainer config={config} className="w-full" chartHeight={height}>
      <LineChart data={chartData} accessibilityLayer>
        {cartesianAxes(labelColumn, axis)}
        {seriesColumns.map((col) => (
          <Line
            key={col}
            type="monotone"
            dataKey={col}
            stroke={config[col]?.color ?? CHART_COLORS[0]}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    </ChartContainer>
  );
}

function renderAreaChart(
  chartData: Record<string, unknown>[],
  labelColumn: string,
  seriesColumns: string[],
  config: ChartConfig,
  axis: AxisConfig,
  height: number,
): React.ReactElement {
  return (
    <ChartContainer config={config} className="w-full" chartHeight={height}>
      <AreaChart data={chartData} accessibilityLayer>
        {cartesianAxes(labelColumn, axis)}
        {seriesColumns.map((col) => (
          <Area
            key={col}
            type="monotone"
            dataKey={col}
            stroke={config[col]?.color ?? CHART_COLORS[0]}
            fill={config[col]?.color ?? CHART_COLORS[0]}
            fillOpacity={0.2}
          />
        ))}
      </AreaChart>
    </ChartContainer>
  );
}

function renderPieChart(
  chartData: Record<string, unknown>[],
  labelColumn: string,
  seriesColumns: string[],
  config: ChartConfig,
  height: number,
): React.ReactElement {
  // For pie charts, use the first series column as the value
  const valueColumn = seriesColumns[0] ?? '';

  return (
    <ChartContainer config={config} className="w-full" chartHeight={height}>
      <PieChart accessibilityLayer>
        <ChartTooltip content={<ChartTooltipContent nameKey={labelColumn} />} />
        <Pie data={chartData} dataKey={valueColumn} nameKey={labelColumn} cx="50%" cy="50%" outerRadius="80%" label>
          {chartData.map((_, index) => (
            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
          ))}
        </Pie>
      </PieChart>
    </ChartContainer>
  );
}

// ─── Sub-renderers ───

function ChartPanel({ panel, data }: { panel: PanelType; data: PanelData }): React.ReactElement {
  // Measure the panel width so the x-axis can decide whether labels fit
  // horizontally (vs. needing to be angled).
  const ref = useRef<HTMLDivElement>(null);
  const width = useContainerWidth(ref);
  const { labelColumn, seriesColumns } = useMemo(() => detectSeriesColumns(data), [data]);
  const config = useMemo(() => buildChartConfig(seriesColumns), [seriesColumns]);
  const chartData = useMemo(
    () => prepareChartData(data, labelColumn, seriesColumns),
    [data, labelColumn, seriesColumns],
  );
  const axis = useMemo(
    () =>
      buildXAxisConfig(
        chartData.map((row) => row[labelColumn]),
        width,
      ),
    [chartData, labelColumn, width],
  );

  if (seriesColumns.length === 0) {
    return <div className="py-4 text-center text-sm text-muted-foreground">No numeric data to chart</div>;
  }

  const chartType = panel.chartType ?? 'bar';

  // Fixed plot height + the axis-label space so angled labels grow the box
  // downward instead of shrinking the plot.
  const cartesianHeight = CHART_PLOT_HEIGHT + axis.axisHeight;

  let chart: React.ReactElement;
  switch (chartType) {
    case 'line':
      chart = renderLineChart(chartData, labelColumn, seriesColumns, config, axis, cartesianHeight);
      break;
    case 'area':
      chart = renderAreaChart(chartData, labelColumn, seriesColumns, config, axis, cartesianHeight);
      break;
    case 'pie':
      chart = renderPieChart(chartData, labelColumn, seriesColumns, config, CHART_PLOT_HEIGHT);
      break;
    case 'bar':
    default:
      chart = renderBarChart(chartData, labelColumn, seriesColumns, config, axis, cartesianHeight);
      break;
  }

  // Pie legends key off the row labels (one swatch per slice); bar/line/area key
  // off the series columns. Rendered below the chart (not inside it) so a wrapped
  // legend grows the card instead of overlapping the plot.
  const legendItems =
    chartType === 'pie'
      ? buildLegendItems(
          chartData.map((row) => coerceLabel(row[labelColumn])),
          CHART_COLORS,
        )
      : buildLegendItems(
          seriesColumns.map((col) => config[col]?.label ?? col),
          seriesColumns.map((col) => config[col]?.color ?? CHART_COLORS[0]!),
        );

  // Cartesian charts pick their angle (and thus height) from the measured width;
  // rendering before the first measurement would use the fallback width and snap
  // to a different height once measured. Gate on width to avoid that layout shift.
  // Pie height is width-independent, so it can render immediately.
  const ready = chartType === 'pie' || width > 0;

  return (
    <div ref={ref} className="w-full">
      {ready && chart}
      {ready && <ChartLegendList items={legendItems} />}
    </div>
  );
}

function TablePanel({ data }: { data: PanelData }): React.ReactElement {
  const columns = useMemo(() => deriveColumns(data.rows, data.columns), [data.rows, data.columns]);
  return <Table data={data.rows} columns={columns} totalRows={data.rowCount} enableCsvExport enableSort showRowCount />;
}

function MetricPanel({
  panel,
  data,
  actions,
}: {
  panel: PanelType;
  data: PanelData;
  actions?: React.ReactNode;
}): React.ReactElement {
  const firstRow = data.rows[0];
  const columns = data.columns;

  // Extract the main metric value from the first numeric column
  let value: number | null = null;
  if (firstRow && columns.length > 0) {
    // Try the first column, then search for a numeric one
    for (const col of columns) {
      const raw = firstRow[col];
      if (isNumeric(raw)) {
        value = toNumber(raw);
        break;
      }
    }
  }

  return (
    <MetricCard
      title={panel.title}
      value={value}
      format={panel.metricConfig?.format}
      prefix={panel.metricConfig?.prefix}
      suffix={panel.metricConfig?.suffix}
      actions={actions}
    />
  );
}

function FunnelPanel({
  panel,
  data,
  actions,
}: {
  panel: PanelType;
  data: PanelData;
  actions?: React.ReactNode;
}): React.ReactElement {
  const stepCol = panel.funnelConfig?.stepColumn ?? data.columns[0] ?? 'step';
  const valueCol = panel.funnelConfig?.valueColumn ?? data.columns[1] ?? 'count';

  const steps = data.rows.map((row) => ({
    label: coerceLabel(row[stepCol]),
    count: toNumber(row[valueCol]),
  }));

  return <FunnelChart title={panel.title} steps={steps} actions={actions} />;
}

// ─── Panel Actions (top-right toolbar) ───

function SqlPreviewBody({ formatted, title }: { formatted: string; title: string }): React.ReactElement {
  const syntaxStyle = useSyntaxStyle();
  return (
    <>
      <div className="flex h-12 items-center justify-between gap-4 border-b py-2 pr-3 pl-5 text-sm font-medium md:pr-10">
        <span className="truncate">{title} - SQL</span>
        <CopyButton text={formatted} copyLabel="SQL" variant="tertiary" iconOnly />
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <SyntaxHighlighter
          language="sql"
          style={syntaxStyle}
          customStyle={{
            margin: 0,
            padding: '1rem 1.25rem',
            fontSize: '0.8125rem',
            lineHeight: '1.6',
          }}
        >
          {formatted}
        </SyntaxHighlighter>
      </div>
    </>
  );
}

function ViewSqlButton({ sql, title }: { sql: string; title: string }): React.ReactElement {
  const formatted = formatSql(sql);
  const isMobile = useIsMobile();

  const trigger = (
    <Button
      variant="tertiary"
      size="sm"
      iconOnly
      Icon={<Code2 />}
      className="text-muted-foreground/60 hover:text-primary"
      aria-label={`View SQL for ${title}`}
    />
  );

  if (isMobile) {
    return (
      <Sheet>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent
          side="bottom"
          showCloseButton={false}
          className="flex max-h-[85vh] flex-col gap-0 overflow-hidden rounded-t-xl bg-card p-0"
        >
          <SheetTitle className="sr-only">{title} - SQL</SheetTitle>
          <SqlPreviewBody formatted={formatted} title={title} />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden rounded-lg bg-card p-0 sm:max-w-3xl">
        <DialogHeader className="sr-only">
          <DialogTitle>{title} - SQL</DialogTitle>
        </DialogHeader>
        <SqlPreviewBody formatted={formatted} title={title} />
      </DialogContent>
    </Dialog>
  );
}

function PanelActions({
  sql,
  title,
  onAskAssistant,
  className = '',
}: {
  /** Public dashboards omit SQL from the projection — when undefined, the
   * View SQL button is skipped so anonymous viewers can not read the query. */
  sql?: string;
  title: string;
  onAskAssistant?: () => void;
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn('flex items-center gap-0.5', className)}>
      {sql !== undefined && <ViewSqlButton sql={sql} title={title} />}
      {onAskAssistant && (
        <Button
          variant="tertiary"
          size="sm"
          iconOnly
          onClick={onAskAssistant}
          Icon={<Sparkles />}
          className="text-muted-foreground/60 hover:text-primary"
          aria-label={`Ask assistant about ${title}`}
        />
      )}
    </div>
  );
}

// ─── Loading Skeleton ───

function LoadingSkeleton({ type }: { type: PanelType['type'] }): React.ReactElement {
  switch (type) {
    case 'metric':
      return (
        <>
          <Skeleton className="h-8 w-24" />
          <Skeleton className="mt-2 h-4 w-16" />
        </>
      );
    case 'table':
      return (
        <div className="space-y-2">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-3/4" />
        </div>
      );
    case 'funnel':
      return (
        <div className="space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-8 w-1/2" />
        </div>
      );
    case 'chart':
    default:
      // Fixed height (≈ CHART_PLOT_HEIGHT + axis + legend) so the skeleton roughly
      // matches the loaded chart and doesn't snap on first paint, unlike aspect-video
      // which scales with width.
      return <Skeleton className="h-80 w-full" />;
  }
}

function TruncationWarning(): React.ReactElement {
  return (
    <p role="status" className="mt-2 text-xs text-warning">
      Showing the first 10,000 rows. This result is incomplete.
    </p>
  );
}

// ─── Main Component ───

export function DashboardPanel({
  panel,
  data,
  loading = false,
  refetching = false,
  error = null,
  problem = null,
  onAskAssistant,
  onFixError,
}: Props): React.ReactElement {
  const busy = loading || refetching;

  // Metric and funnel panels have their own Card wrapper
  const usesOwnCard = panel.type === 'metric' || panel.type === 'funnel';

  // Metric and funnel own their Card, so their toolbar goes through the card's header slot —
  // an absolutely-positioned overlay could not line up with a title it does not share a row with.
  // `shrink-0` keeps the buttons whole when a long title has to truncate beside them.
  const ownCardActions = (
    <PanelActions sql={panel.sql} title={panel.title} onAskAssistant={onAskAssistant} className="shrink-0" />
  );

  if (usesOwnCard && !busy && !error && data) {
    // The toolbar rides the card's own header slot, so there is no wrapper to hang the truncation
    // notice on — a fragment keeps it directly under the card, where the other paths put it.
    const ownCard =
      panel.type === 'metric' ? (
        <MetricPanel panel={panel} data={data} actions={ownCardActions} />
      ) : panel.type === 'funnel' ? (
        <FunnelPanel panel={panel} data={data} actions={ownCardActions} />
      ) : null;

    if (!ownCard) return <></>;
    return (
      <>
        {ownCard}
        {data.truncated && <TruncationWarning />}
      </>
    );
  }

  if (usesOwnCard && busy && !error) {
    if (panel.type === 'metric') {
      return <MetricCard title={panel.title} value={null} loading />;
    }
    return (
      <Card aria-busy>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">{panel.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <LoadingSkeleton type={panel.type} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card aria-busy={busy}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground">{panel.title}</CardTitle>
          {!busy && <PanelActions sql={panel.sql} title={panel.title} onAskAssistant={onAskAssistant} />}
        </div>
      </CardHeader>
      <CardContent>
        {busy && (
          <div role="status" aria-label={`Loading ${panel.title}`}>
            <LoadingSkeleton type={panel.type} />
          </div>
        )}
        {error && (
          <div className="flex items-start gap-2 py-2">
            {problem ? (
              <div className="min-w-0 flex-1">
                <WarehouseProblemCallout problem={problem} />
              </div>
            ) : (
              <span className="flex-1 text-sm text-danger">{error}</span>
            )}
            {onFixError && (
              <Button
                variant="secondary"
                size="sm"
                onClick={onFixError}
                suffixIcon={<Sparkles />}
                className="shrink-0"
                label="Fix"
              />
            )}
          </div>
        )}
        {!busy && !error && data && (
          <>
            {panel.type === 'chart' && <ChartPanel panel={panel} data={data} />}
            {panel.type === 'table' && <TablePanel data={data} />}
            {data.truncated && <TruncationWarning />}
          </>
        )}
        {!busy && !error && !data && (
          <div className="py-4 text-center text-sm text-muted-foreground">No data available</div>
        )}
      </CardContent>
    </Card>
  );
}
